import React, { ComponentProps, useCallback, useMemo } from 'react';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';

import AlertStatic from 'buyside/components/presentation/AlertStatic/alertStatic';
import BasicTable, { Column, ROW_HEIGHTS } from 'buyside/components/presentation/Table/basicTable';
import Button, { BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import Modal from 'buyside/components/presentation/Modal/modal';

import {
  EmployerProfile,
  useAllocatorBlocklistQuery,
  useDestroyBlocklistEntryMutation
} from 'interfaces/graphql-typings';

const AllocatorBlocklistModal: React.FC<{
  buttonOptions?: Partial<ComponentProps<typeof Button>>;
  fundProfileId: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ buttonOptions, fundProfileId, isOpen, onClose }) => {
  const [handleDestroyBlocklistEntry] = useDestroyBlocklistEntryMutation({
    update: (cache, result) =>
      cache.modify<EmployerProfile>({
        id: `EmployerProfile:${fundProfileId}`,
        fields: {
          allocatorBlocklist: (existingBlocklist, { readField }) => {
            const destroyedBlocklistEntryId =
              result.data?.destroyAllocatorBlocklistEntry?.destroyedBlocklistEntry?.id;
            if (!destroyedBlocklistEntryId) return existingBlocklist;

            return existingBlocklist.filter(
              blocklist => destroyedBlocklistEntryId !== readField('id', blocklist)
            );
          }
        }
      })
  });

  const { data, loading } = useAllocatorBlocklistQuery({ variables: { id: fundProfileId } });

  const { allocatorBlocklist } = data?.fundProfile || {};

  const destroyBlocklistEntry = useCallback(
    (allocatorProfileId: string, employerProfileId: string): void => {
      handleDestroyBlocklistEntry({
        variables: { allocatorProfileId, employerProfileId }
      });
    },
    [handleDestroyBlocklistEntry]
  );

  const columns = useMemo(
    () => [
      allocatorBlocklist?.map(entry => entry.allocatorProfile.name),
      allocatorBlocklist?.map(entry => (
        <Button
          id={`remove-blocklist-entry-${entry.allocatorProfile.id}`}
          faIcon={faTrash}
          onClick={(): void => destroyBlocklistEntry(entry.allocatorProfile.id, fundProfileId)}
          size={BUTTON_SIZES.TINIEST}
          text="Remove"
        />
      ))
    ],
    [allocatorBlocklist, destroyBlocklistEntry, fundProfileId]
  );

  const renderOpenModalButton = useCallback(
    () => (buttonOptions ? <Button {...buttonOptions} /> : null),
    [buttonOptions]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      renderOpenModalButton={renderOpenModalButton}
      title="Manage Allocator Blocklist"
    >
      <div>
        <h5>What is an Allocator Blocklist?</h5>
        <p>
          Allocators you add to this list cannot see the sections on your Fund's Profile specified
          for "Filtered Allocators".
        </p>
        <h5>How do I add an allocator to the list?</h5>
        <p>Navigate to the Allocator's Profile, and click on "Add to Your Fund's Blocklist"</p>
      </div>
      <h5>Remove Allocators from Blocklist:</h5>
      <div>
        {loading ? (
          <LoadingSpinner />
        ) : allocatorBlocklist?.length ? (
          <BasicTable columns={columns} rowHeight={ROW_HEIGHTS.LARGE}>
            <Column idx={0} heading="Allocator Name" />
            <Column idx={1} heading="Remove from Blocklist" />
          </BasicTable>
        ) : (
          <AlertStatic>Your Fund's Blocklist is Empty</AlertStatic>
        )}
      </div>
    </Modal>
  );
};

export default AllocatorBlocklistModal;
